import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { DateTime } from 'luxon';
import { Period } from 'graphql/types';
export enum SmartDiaryViewOptions {
  Custom,
  Today,
  CurrentPeriod
}

type SmartTimesheetContextValue = {
  timesheetPeriods: Period[];
  setTimesheetPeriods: (periods: Period[]) => void;
  currentPeriod: Period | null;
  setCurrentPeriod: (period: Period) => void;
  displayTimesheetPeriods: boolean;
  selectedViewOption: SmartDiaryViewOptions;
  setSelectedViewOption: (selectedViewOption: SmartDiaryViewOptions) => void;
  isSmartDiarySelectionUpdated: boolean;
  setSmartDiarySelectionUpdated: React.Dispatch<React.SetStateAction<boolean>>;
};

export type SmartTimesheetProviderType = {
  children?: React.ReactNode;
  displayPeriods?: boolean;
};
export type DateRange = { startDate: DateTime; endDate: DateTime };

export const SmartTimesheetContext = createContext<SmartTimesheetContextValue | null>(
  null
);

export const useSmartTimesheetContext = (): SmartTimesheetContextValue => {
  const value = useContext(SmartTimesheetContext);
  if (!value) {
    throw Error('should be called in SmartTimesheetContextProvider scope');
  }
  return value;
};

export const updateCurrentPeriod = ({
  displayTimesheetPeriods,
  periods,
  setCurrentPeriod
}: {
  displayTimesheetPeriods: boolean;
  periods: Period[];
  setCurrentPeriod: (period: Period) => void;
}): void => {
  if (displayTimesheetPeriods) {
    setCurrentPeriod(periods[periods.length - 1]);
  } else {
    setCurrentPeriod({
      startDate: DateTime.local().toISODate(),
      endDate: DateTime.local().toISODate()
    });
  }
};

const SmartTimesheetContextProvider: React.FC<SmartTimesheetProviderType> = ({
  children,
  displayPeriods = true
}: SmartTimesheetProviderType) => {
  const [periods, setPeriods] = useState<Period[]>([]);
  const [
    isSmartDiarySelectionUpdated,
    setSmartDiarySelectionUpdated
  ] = useState(false);

  const [currentPeriod, setCurrentPeriod] = useState<Period | null>(
    displayPeriods
      ? null
      : {
          startDate: DateTime.local().toISODate(),
          endDate: DateTime.local().toISODate()
        }
  );
  const [displayTimesheetPeriods, setDisplayTimesheetPeriods] = useState(
    displayPeriods
  );
  const [selectedViewOption, setSelectedOption] = useState(
    displayPeriods
      ? SmartDiaryViewOptions.CurrentPeriod
      : SmartDiaryViewOptions.Today
  );

  const setSelectedViewOption = useCallback(
    (selectedViewOption: SmartDiaryViewOptions) => {
      setSelectedOption(selectedViewOption);
      if (selectedViewOption !== SmartDiaryViewOptions.Custom) {
        const displayTimesheetPeriods =
          selectedViewOption === SmartDiaryViewOptions.CurrentPeriod;
        setDisplayTimesheetPeriods(displayTimesheetPeriods);
        updateCurrentPeriod({
          displayTimesheetPeriods,
          periods,
          setCurrentPeriod
        });
      }
    },
    [periods]
  );

  const value = useMemo(
    () => ({
      timesheetPeriods: periods ?? [],
      setTimesheetPeriods: setPeriods,
      currentPeriod,
      setCurrentPeriod,
      displayTimesheetPeriods,
      selectedViewOption,
      setSelectedViewOption,
      isSmartDiarySelectionUpdated,
      setSmartDiarySelectionUpdated
    }),
    [
      currentPeriod,
      displayTimesheetPeriods,
      isSmartDiarySelectionUpdated,
      periods,
      selectedViewOption,
      setSelectedViewOption
    ]
  );

  return (
    <SmartTimesheetContext.Provider value={value}>
      {children}
    </SmartTimesheetContext.Provider>
  );
};

export default SmartTimesheetContextProvider;
